// Islamic Calendar Utilities
export const hijriMonths = [
  'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani',
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', 'Shaban',
  'Ramadan', 'Shawwal', 'Dhul Qadah', 'Dhul Hijjah'
];

export const hijriMonthsArabic = [
  'محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني',
  'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان',
  'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
];

// Convert Gregorian to Hijri (approximate algorithm)
export const gregorianToHijri = (date = new Date()) => {
  const jd = Math.floor((date.getTime() / 86400000) + 2440587.5);
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor((50 * l2) / 17719) +
            Math.floor(l2 / 5670) * Math.floor((43 * l2) / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
             Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const month = Math.floor((24 * l3) / 709);
  const day = l3 - Math.floor((709 * month) / 24);
  const year = 30 * n + j - 30;
  
  return { day, month, year };
};

export const formatHijriDate = (hijri, lang = 'en') => {
  const months = lang === 'ar' || lang === 'ur' ? hijriMonthsArabic : hijriMonths;
  return `${hijri.day} ${months[hijri.month - 1]} ${hijri.year} AH`;
};

// Business types configuration
export const businessTypes = {
  hospitality: {
    label: 'Hospitality & Food',
    labelAr: 'الضيافة والطعام',
    types: [
      { id: 'hotel', name: 'Hotel / Resort', nameAr: 'فندق / منتجع', icon: '🏨', method: 'hybrid', methodLabel: 'Hybrid Assets + Revenue' },
      { id: 'restaurant', name: 'Restaurant / Café', nameAr: 'مطعم / مقهى', icon: '🍽️', method: 'assets', methodLabel: 'Trade Goods + Cash' },
      { id: 'catering', name: 'Catering / Food Prod.', nameAr: 'تموين / إنتاج غذائي', icon: '🍱', method: 'assets', methodLabel: 'Inventory + Receivables' }
    ]
  },
  retail: {
    label: 'Retail & Trade',
    labelAr: 'البيع بالتجزئة والتجارة',
    types: [
      { id: 'retail', name: 'Retail Shop / Store', nameAr: 'متجر تجزئة', icon: '🛍️', method: 'assets', methodLabel: 'Stock + Cash + Receivables' },
      { id: 'wholesale', name: 'Wholesale / Import', nameAr: 'جملة / استيراد', icon: '📦', method: 'assets', methodLabel: 'Trade Goods at Cost' },
      { id: 'ecommerce', name: 'E-Commerce / Online', nameAr: 'تجارة إلكترونية', icon: '💻', method: 'assets', methodLabel: 'Stock + Digital Assets' },
      { id: 'jewelry', name: 'Jewellery Shop', nameAr: 'محل مجوهرات', icon: '💍', method: 'special', methodLabel: 'Gold/Silver Weight × Price' }
    ]
  },
  property: {
    label: 'Property & Construction',
    labelAr: 'العقارات والبناء',
    types: [
      { id: 'property_dev', name: 'Property Developer', nameAr: 'مطور عقاري', icon: '🏗️', method: 'assets', methodLabel: 'Units for Sale at Market' },
      { id: 'rental', name: 'Rental Property Owner', nameAr: 'مالك عقار للإيجار', icon: '🔑', method: 'revenue', methodLabel: 'Income Accumulated' },
      { id: 'construction', name: 'Contractor / Builder', nameAr: 'مقاول / بناء', icon: '🧱', method: 'net', methodLabel: 'Liquid Assets + Billings' }
    ]
  },
  agriculture: {
    label: 'Agriculture & Livestock',
    labelAr: 'الزراعة والمواشي',
    types: [
      { id: 'farm', name: 'Farm / Crop Producer', nameAr: 'مزرعة / منتج محاصيل', icon: '🌾', method: 'harvest', methodLabel: '10% or 5% at Harvest' },
      { id: 'livestock', name: 'Livestock / Ranching', nameAr: 'مواشي / رعي', icon: '🐄', method: 'perhead', methodLabel: 'Per Head Count' },
      { id: 'fishery', name: 'Fishery / Aquaculture', nameAr: 'صيد / استزراع مائي', icon: '🐟', method: 'revenue', methodLabel: 'Net Revenue (Contemporary)' }
    ]
  },
  manufacturing: {
    label: 'Manufacturing & Industry',
    labelAr: 'التصنيع والصناعة',
    types: [
      { id: 'factory', name: 'Factory / Manufacturing', nameAr: 'مصنع / تصنيع', icon: '🏭', method: 'net', methodLabel: 'Liquid Assets Only' },
      { id: 'mining', name: 'Mining / Resources', nameAr: 'تعدين / موارد', icon: '⛏️', method: 'special', methodLabel: 'Rikaz 20% / Khums' }
    ]
  },
  services: {
    label: 'Services & Finance',
    labelAr: 'الخدمات والمالية',
    types: [
      { id: 'professional', name: 'Professional Services', nameAr: 'خدمات مهنية', icon: '⚖️', method: 'assets', methodLabel: 'Cash + Receivables' },
      { id: 'transport', name: 'Transport / Logistics', nameAr: 'نقل / لوجستيات', icon: '🚛', method: 'net', methodLabel: 'Liquid Assets Only' },
      { id: 'clinic', name: 'Clinic / Hospital', nameAr: 'عيادة / مستشفى', icon: '🏥', method: 'assets', methodLabel: 'Cash + Receivables' },
      { id: 'school', name: 'School / Institution', nameAr: 'مدرسة / مؤسسة', icon: '🎓', method: 'revenue', methodLabel: 'Net Savings Accumulated' },
      { id: 'tech', name: 'Tech / SaaS / Startup', nameAr: 'تقنية / برمجيات', icon: '💡', method: 'net', methodLabel: 'Liquid Assets + IP' },
      { id: 'investment', name: 'Investment Company', nameAr: 'شركة استثمار', icon: '📈', method: 'lookthrough', methodLabel: 'Look-Through Method' }
    ]
  }
};

// Asset field configurations by business type
export const businessAssetFields = {
  hotel: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', labelAr: 'النقد والأرصدة البنكية', include: true },
      { id: 'receivables', label: 'Accounts Receivable (OTA, Corporate)', labelAr: 'المستحقات', include: true },
      { id: 'fbInventory', label: 'Food & Beverage Inventory', labelAr: 'مخزون الطعام والشراب', include: true },
      { id: 'giftShop', label: 'Gift Shop / Retail Inventory', labelAr: 'مخزون محل الهدايا', include: true },
      { id: 'deposits', label: 'Advance Bookings / Guest Deposits', labelAr: 'الحجوزات المقدمة', include: true },
      { id: 'investments', label: 'Investments / Fixed Deposits', labelAr: 'استثمارات / ودائع', include: true }
    ],
    exempt: [
      { id: 'building', label: 'Building / Land Value', labelAr: 'قيمة المبنى / الأرض' },
      { id: 'furniture', label: 'Furniture & Fixtures', labelAr: 'الأثاث والتركيبات' },
      { id: 'vehicles', label: 'Vehicles / Equipment', labelAr: 'المركبات / المعدات' }
    ],
    deductions: [
      { id: 'suppliers', label: 'Outstanding Supplier Invoices', labelAr: 'فواتير الموردين' },
      { id: 'loans', label: 'Short-term Bank Loans (Trade)', labelAr: 'قروض قصيرة الأجل' },
      { id: 'salaries', label: 'Salaries Payable', labelAr: 'الرواتب المستحقة' },
      { id: 'taxes', label: 'Tax Liabilities Due', labelAr: 'الضرائب المستحقة' }
    ],
    ruling: 'The fixed assets (building, furniture, equipment) used in the trade are exempt. Zakat applies to liquid trade assets: cash, receivables, guest deposits, and trade inventory.',
    rulingAr: 'الأصول الثابتة (المبنى، الأثاث، المعدات) المستخدمة في التجارة معفاة. تُطبق الزكاة على الأصول التجارية السائلة: النقد، المستحقات، ودائع الضيوف، والمخزون التجاري.'
  },
  restaurant: {
    assets: [
      { id: 'cash', label: 'Cash (Till + Bank)', labelAr: 'النقد (الصندوق + البنك)', include: true },
      { id: 'fbInventory', label: 'Food & Beverage Inventory (at cost)', labelAr: 'مخزون الطعام (بالتكلفة)', include: true },
      { id: 'merchandise', label: 'Packaged Goods / Merchandise', labelAr: 'البضائع المعبأة', include: true },
      { id: 'receivables', label: 'Catering Receivables', labelAr: 'مستحقات التموين', include: true },
      { id: 'platformBalance', label: 'Delivery Platform Balances', labelAr: 'أرصدة منصات التوصيل', include: true },
      { id: 'savings', label: 'Savings / Short-Term Deposits', labelAr: 'المدخرات / الودائع قصيرة الأجل', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Supplier Invoices Owed', labelAr: 'فواتير الموردين المستحقة' },
      { id: 'rent', label: 'Rent & Utilities Due', labelAr: 'الإيجار والمرافق المستحقة' }
    ],
    ruling: 'A restaurant\'s zakatable wealth is its trade goods (food inventory held for sale), cash in hand and bank, and any outstanding receivables. Kitchen equipment and furniture are exempt.',
    rulingAr: 'ثروة المطعم الخاضعة للزكاة هي سلعه التجارية (مخزون الطعام للبيع)، والنقد في اليد والبنك، وأي مستحقات. معدات المطبخ والأثاث معفاة.'
  },
  retail: {
    assets: [
      { id: 'stock', label: 'Stock / Inventory (Market Value)', labelAr: 'المخزون (القيمة السوقية)', include: true },
      { id: 'stockInTransit', label: 'Stock on Order / In Transit', labelAr: 'المخزون في الطريق', include: true },
      { id: 'cash', label: 'Cash in Tills & Bank', labelAr: 'النقد في الصندوق والبنك', include: true },
      { id: 'receivables', label: 'Trade Receivables (Credit Sales)', labelAr: 'المستحقات التجارية', include: true },
      { id: 'personalCash', label: 'Personal Cash Held', labelAr: 'النقد الشخصي المحتفظ به', include: true },
      { id: 'savings', label: 'Business Fixed Deposits', labelAr: 'ودائع العمل الثابتة', include: true }
    ],
    deductions: [
      { id: 'supplierCredit', label: 'Supplier Credit Owed', labelAr: 'ائتمان الموردين المستحق' },
      { id: 'borrowings', label: 'Short-term Borrowings', labelAr: 'الاقتراض قصير الأجل' },
      { id: 'rent', label: 'Outstanding Rent / Overhead', labelAr: 'الإيجار المستحق' },
      { id: 'wages', label: 'Employee Wages Due', labelAr: 'أجور الموظفين المستحقة' }
    ],
    ruling: 'Retail trade is the clearest case of Zakat al-Tijarah. All goods held for sale at market/cost value, plus cash and receivables, minus trade debts.',
    rulingAr: 'تجارة التجزئة هي الحالة الأوضح لزكاة التجارة. جميع السلع المعدة للبيع بالقيمة السوقية/التكلفة، بالإضافة إلى النقد والمستحقات، مطروحاً منها ديون التجارة.'
  },
  farm: {
    assets: [
      { id: 'harvestQty', label: 'Total Harvest Quantity (kg)', labelAr: 'كمية المحصول (كجم)', type: 'weight' },
      { id: 'pricePerKg', label: 'Market Value per kg', labelAr: 'القيمة السوقية للكيلو', type: 'currency' },
      { id: 'waterSource', label: 'Water Source', labelAr: 'مصدر المياه', type: 'select', options: [
        { value: 0.10, label: 'Rain-fed / Natural (10%)', labelAr: 'مطري / طبيعي (10%)' },
        { value: 0.05, label: 'Irrigated / Pumped (5%)', labelAr: 'مروي / بمضخة (5%)' },
        { value: 0.075, label: 'Mixed (7.5%)', labelAr: 'مختلط (7.5%)' }
      ]},
      { id: 'harvestsPerYear', label: 'Harvests Per Year', labelAr: 'المحاصيل في السنة', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]}
    ],
    deductions: [
      { id: 'productionCosts', label: 'Production Costs (Hanafi)', labelAr: 'تكاليف الإنتاج (حنفي)' }
    ],
    ruling: 'Zakat al-Zuru\' is due at each harvest. No lunar year condition (hawl) applies. Rain-fed land: 10%. Irrigated land: 5%. Mixed: 7.5%. Nisab: 653kg of wheat or equivalent.',
    rulingAr: 'زكاة الزروع تجب عند كل حصاد. لا يُشترط الحول. الأرض المطرية: 10%. الأرض المروية: 5%. المختلطة: 7.5%. النصاب: 653 كجم من القمح أو ما يعادله.',
    nisab: 653,
    hawl: false
  },
  livestock: {
    assets: [
      { id: 'camels', label: 'Camels (Nisab: 5)', labelAr: 'الإبل (النصاب: 5)', type: 'count', nisab: 5 },
      { id: 'cattle', label: 'Cattle / Buffalo (Nisab: 30)', labelAr: 'الأبقار (النصاب: 30)', type: 'count', nisab: 30 },
      { id: 'sheep', label: 'Sheep (Nisab: 40)', labelAr: 'الأغنام (النصاب: 40)', type: 'count', nisab: 40 },
      { id: 'goats', label: 'Goats', labelAr: 'الماعز', type: 'count' },
      { id: 'sheepValue', label: 'Value per Sheep (for cash)', labelAr: 'قيمة الشاة (للنقد)', type: 'currency' }
    ],
    ruling: 'Zakat on livestock applies to camels, cattle, sheep, and goats kept for grazing (sa\'ima) — not working animals. Specific nisab and rates per animal category apply.',
    rulingAr: 'زكاة الماشية تنطبق على الإبل والأبقار والأغنام والماعز السائمة - وليس حيوانات العمل. تطبق نصب ومعدلات محددة لكل فئة حيوانية.'
  },
  catering: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'foodInventory', label: 'Food & Ingredient Inventory (at cost)', include: true },
      { id: 'receivables', label: 'Outstanding Catering Invoices', include: true },
      { id: 'packagingStock', label: 'Packaging & Disposables Stock', include: true },
      { id: 'deposits', label: 'Advance Event Deposits Held', include: true },
      { id: 'savings', label: 'Business Savings / FDs', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Supplier Invoices Owed' },
      { id: 'rent', label: 'Kitchen Rent & Utilities Due' },
      { id: 'wages', label: 'Staff Wages Due' }
    ],
    ruling: 'Catering businesses pay zakat on trade inventory (food/packaging for sale), cash, receivables, and advance deposits. Kitchen equipment and vehicles are exempt fixed assets.'
  },
  wholesale: {
    assets: [
      { id: 'stock', label: 'Wholesale Inventory (at cost or market)', include: true },
      { id: 'stockInTransit', label: 'Goods in Transit / on Order', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Trade Receivables (credit customers)', include: true },
      { id: 'deposits', label: 'LC / Import Deposits', include: true },
      { id: 'savings', label: 'Business Fixed Deposits', include: true }
    ],
    deductions: [
      { id: 'supplierCredit', label: 'Supplier Credit / LC Payable' },
      { id: 'loans', label: 'Trade Finance / Short-term Loans' },
      { id: 'customs', label: 'Customs Duties Owed' },
      { id: 'wages', label: 'Staff Wages Due' }
    ],
    ruling: 'Wholesale/import businesses pay zakat on all trade goods (at market value or cost, whichever is more prudent), plus cash and receivables. Warehouse and transport equipment are exempt.'
  },
  ecommerce: {
    assets: [
      { id: 'stock', label: 'Physical Inventory / Warehouse Stock', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'platformBalance', label: 'Platform Balances (Shopify, Amazon, etc.)', include: true },
      { id: 'receivables', label: 'Pending Payouts / Receivables', include: true },
      { id: 'digitalAssets', label: 'Digital Products Inventory (if resellable)', include: true },
      { id: 'savings', label: 'Business Savings', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Supplier Invoices Owed' },
      { id: 'refunds', label: 'Pending Customer Refunds' },
      { id: 'subscriptions', label: 'Platform Fees / Subscriptions Due' }
    ],
    ruling: 'E-commerce businesses pay zakat on inventory (physical or digital held for sale), cash, platform balances, and receivables. Website, software, and equipment costs are exempt fixed assets.'
  },
  jewelry: {
    assets: [
      { id: 'goldStock', label: 'Gold Stock for Sale (grams)', type: 'weight', metalType: 'gold', include: true },
      { id: 'silverStock', label: 'Silver Stock for Sale (grams)', type: 'weight', metalType: 'silver', include: true },
      { id: 'gemstones', label: 'Gemstones & Precious Stones (market value)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Receivables / Layaway Balances', include: true },
      { id: 'scrapGold', label: 'Scrap Gold / Work-in-Progress (grams)', type: 'weight', metalType: 'gold', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Gold/Silver Supplier Credit' },
      { id: 'loans', label: 'Business Loans (short-term)' },
      { id: 'wages', label: 'Craftsman Wages Due' }
    ],
    ruling: 'Jewellery shops pay zakat on all gold and silver held for trade (by weight × current market price), plus gemstones at market value, cash, and receivables. Display tools and safes are exempt.'
  },
  property_dev: {
    assets: [
      { id: 'unitsForSale', label: 'Completed Units for Sale (market value)', include: true },
      { id: 'workinProgress', label: 'Units Under Construction (cost to date)', include: true },
      { id: 'landBank', label: 'Land Purchased for Development', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Buyer Instalments / Receivables', include: true },
      { id: 'deposits', label: 'Escrow / Security Deposits Held', include: true }
    ],
    deductions: [
      { id: 'constructionPayable', label: 'Contractor Invoices Owed' },
      { id: 'loans', label: 'Project Finance / Bank Loans (current portion)' },
      { id: 'buyerDeposits', label: 'Refundable Buyer Deposits' },
      { id: 'govFees', label: 'Government Fees & Permits Owed' }
    ],
    ruling: 'Property developers pay zakat on all units held for sale (at market value), land purchased for development, work-in-progress, cash, and receivables. Properties held for personal use or rental are NOT trade goods and are exempt from this calculation.',
    exempt: [
      { id: 'officeBuilding', label: 'Office / Showroom (own use)' },
      { id: 'equipment', label: 'Construction Equipment' },
      { id: 'rentalProperty', label: 'Properties Held for Rental (not sale)' }
    ]
  },
  rental: {
    assets: [
      { id: 'rentalIncome', label: 'Accumulated Rental Income (saved)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Outstanding Rent from Tenants', include: true },
      { id: 'deposits', label: 'Security Deposits Held', include: true },
      { id: 'savings', label: 'Savings from Rental Income', include: true }
    ],
    deductions: [
      { id: 'maintenance', label: 'Maintenance / Repair Bills Due' },
      { id: 'mortgage', label: 'Mortgage Instalments (current year)' },
      { id: 'propertyTax', label: 'Property Tax / Municipal Charges' },
      { id: 'agentFees', label: 'Agent Commission Due' }
    ],
    ruling: 'Rental property owners do NOT pay zakat on the property itself — only on the accumulated rental income that has been saved for one lunar year. Outstanding rent receivables are also zakatable.',
    exempt: [
      { id: 'propertyValue', label: 'Property Value (not for sale)' },
      { id: 'fixtures', label: 'Furniture & Fixtures in Property' }
    ]
  },
  construction: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Progress Billing Receivables', include: true },
      { id: 'retentions', label: 'Retention Money Due', include: true },
      { id: 'materials', label: 'Construction Materials on Hand (for sale/use)', include: true },
      { id: 'deposits', label: 'Project Advance Deposits', include: true }
    ],
    deductions: [
      { id: 'subcontractors', label: 'Subcontractor Invoices Owed' },
      { id: 'materialSuppliers', label: 'Material Supplier Credit' },
      { id: 'loans', label: 'Project Loans (current portion)' },
      { id: 'wages', label: 'Labour Wages Due' }
    ],
    ruling: 'Contractors pay zakat on liquid assets: cash, receivables (progress billings), retention money, and materials held for trade. Heavy equipment, vehicles, and tools used in work are exempt fixed assets.'
  },
  factory: {
    assets: [
      { id: 'rawMaterials', label: 'Raw Materials Stock', include: true },
      { id: 'finishedGoods', label: 'Finished Goods Inventory', include: true },
      { id: 'workinProgress', label: 'Work-in-Progress (value)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Trade Receivables', include: true },
      { id: 'savings', label: 'Business Fixed Deposits', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Raw Material Supplier Credit' },
      { id: 'loans', label: 'Working Capital Loans' },
      { id: 'wages', label: 'Employee Wages / Benefits Due' },
      { id: 'taxes', label: 'Tax Liabilities Due' }
    ],
    ruling: 'Factories pay zakat on trade goods: raw materials, finished goods, and work-in-progress — all valued at current market price. Machinery, buildings, and equipment are exempt fixed assets.',
    exempt: [
      { id: 'machinery', label: 'Machinery & Equipment' },
      { id: 'building', label: 'Factory Building' },
      { id: 'vehicles', label: 'Transport Vehicles (own use)' }
    ]
  },
  mining: {
    assets: [
      { id: 'extractedMinerals', label: 'Extracted Minerals / Resources (value)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Buyer Receivables', include: true },
      { id: 'stockpile', label: 'Stockpiled Resources (market value)', include: true }
    ],
    deductions: [
      { id: 'operatingCosts', label: 'Operating Expenses Due' },
      { id: 'royalties', label: 'Government Royalties Owed' },
      { id: 'loans', label: 'Business Loans (current portion)' }
    ],
    ruling: 'Mining/rikaz: If minerals are found treasure (rikaz), the rate is 20% (khums) with no hawl. For commercial mining operations, zakat is 2.5% on extracted resources held for sale, cash, and receivables.'
  },
  professional: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Outstanding Client Invoices', include: true },
      { id: 'retainers', label: 'Retainer Fees Collected', include: true },
      { id: 'savings', label: 'Business Savings / FDs', include: true },
      { id: 'unbilledWork', label: 'Unbilled Work-in-Progress', include: true }
    ],
    deductions: [
      { id: 'rent', label: 'Office Rent & Utilities Due' },
      { id: 'wages', label: 'Staff Salaries Due' },
      { id: 'taxes', label: 'Professional Tax / VAT Due' },
      { id: 'loans', label: 'Business Loans (current portion)' }
    ],
    ruling: 'Professional service firms (law, accounting, consulting) pay zakat on liquid assets: cash, receivables, retainers, and savings. Office furniture, computers, and professional library are exempt.'
  },
  transport: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Freight / Trip Receivables', include: true },
      { id: 'fuelStock', label: 'Fuel Stock (if held for resale)', include: true },
      { id: 'sparePartsForSale', label: 'Spare Parts (if held for resale)', include: true },
      { id: 'savings', label: 'Business Savings', include: true }
    ],
    deductions: [
      { id: 'fuelCredit', label: 'Fuel Supplier Credit' },
      { id: 'maintenance', label: 'Vehicle Maintenance Due' },
      { id: 'loans', label: 'Vehicle Finance (current portion)' },
      { id: 'wages', label: 'Driver Wages Due' }
    ],
    ruling: 'Transport/logistics companies pay zakat on liquid assets only: cash, receivables, and savings. Vehicles, trucks, and equipment used for operations are exempt fixed assets — they are NOT trade goods unless held for resale.',
    exempt: [
      { id: 'vehicles', label: 'Fleet Vehicles (operational use)' },
      { id: 'equipment', label: 'Logistics Equipment' }
    ]
  },
  clinic: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Patient / Insurance Receivables', include: true },
      { id: 'pharmaStock', label: 'Pharmacy Stock (for sale)', include: true },
      { id: 'consumables', label: 'Medical Consumables Stock', include: true },
      { id: 'savings', label: 'Business Savings / FDs', include: true }
    ],
    deductions: [
      { id: 'suppliers', label: 'Medical Supplier Credit' },
      { id: 'wages', label: 'Staff Salaries Due' },
      { id: 'rent', label: 'Premises Rent Due' },
      { id: 'insurance', label: 'Insurance Premiums Due' }
    ],
    ruling: 'Clinics/hospitals pay zakat on liquid assets: cash, receivables, pharmacy stock (held for sale), and savings. Medical equipment, furniture, and the building are exempt.',
    exempt: [
      { id: 'medicalEquipment', label: 'Medical Equipment & Devices' },
      { id: 'building', label: 'Clinic/Hospital Building' }
    ]
  },
  school: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Outstanding Tuition Fees', include: true },
      { id: 'bookStock', label: 'Bookshop / Stationery Stock (for sale)', include: true },
      { id: 'savings', label: 'Accumulated Fee Income (saved)', include: true },
      { id: 'deposits', label: 'Fixed Deposits / Investments', include: true }
    ],
    deductions: [
      { id: 'wages', label: 'Teacher Salaries Due' },
      { id: 'rent', label: 'Premises Rent Due' },
      { id: 'maintenance', label: 'Maintenance / Utility Bills' },
      { id: 'refunds', label: 'Fee Refunds Owed to Students' }
    ],
    ruling: 'Schools/institutions pay zakat on accumulated savings from fee income, cash, receivables, and any goods held for sale (bookshop stock). The building, furniture, and educational equipment are exempt.'
  },
  tech: {
    assets: [
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Customer / SaaS Receivables', include: true },
      { id: 'prepaidSubscriptions', label: 'Prepaid Annual Subscriptions Received', include: true },
      { id: 'investments', label: 'Treasury / Short-term Investments', include: true },
      { id: 'savings', label: 'Business Savings / FDs', include: true }
    ],
    deductions: [
      { id: 'hostingCosts', label: 'Hosting / Cloud Services Due' },
      { id: 'wages', label: 'Employee Salaries Due' },
      { id: 'loans', label: 'Venture Debt / Loans (current portion)' },
      { id: 'refunds', label: 'Customer Refunds Owed' }
    ],
    ruling: 'Tech/SaaS companies pay zakat on liquid assets: cash, receivables, investments, and prepaid subscription income. Software IP, code, hardware, and equipment are exempt (unless held for resale).',
    exempt: [
      { id: 'ipSoftware', label: 'Software / IP (not for resale)' },
      { id: 'hardware', label: 'Servers & Hardware' }
    ]
  },
  investment: {
    assets: [
      { id: 'listedStocks', label: 'Listed Stock Portfolio (market value)', include: true },
      { id: 'privateEquity', label: 'Private Equity / Unlisted Shares', include: true },
      { id: 'bonds', label: 'Bonds / Sukuk (face + accrued profit)', include: true },
      { id: 'funds', label: 'Mutual Funds / ETFs (NAV)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Dividend / Interest Receivables', include: true },
      { id: 'savings', label: 'Fixed Deposits / Treasury Bills', include: true }
    ],
    deductions: [
      { id: 'marginLoans', label: 'Margin Loans / Broker Credit' },
      { id: 'managementFees', label: 'Fund Management Fees Due' },
      { id: 'taxes', label: 'Capital Gains Tax Owed' },
      { id: 'loans', label: 'Investment Loans (current portion)' }
    ],
    ruling: 'Investment companies use the look-through method: zakat on the zakatable portion of each holding. For trading portfolios: full market value. For investment holdings: proportional share of underlying cash + receivables. Cash, bonds, and fund NAV are fully zakatable.'
  },
  fishery: {
    assets: [
      { id: 'fishStock', label: 'Fish Stock / Harvest (market value)', include: true },
      { id: 'cash', label: 'Cash & Bank Balances', include: true },
      { id: 'receivables', label: 'Buyer Receivables', include: true },
      { id: 'feedStock', label: 'Fish Feed Stock (for resale)', include: true },
      { id: 'savings', label: 'Business Savings', include: true }
    ],
    deductions: [
      { id: 'feedSuppliers', label: 'Feed Supplier Credit' },
      { id: 'wages', label: 'Worker Wages Due' },
      { id: 'maintenance', label: 'Pond / Equipment Maintenance Due' }
    ],
    ruling: 'Fishery/aquaculture is a contemporary business — scholars treat it as trade goods. Zakat applies to fish stock held for sale (market value), cash, and receivables. Ponds, boats, and equipment are exempt fixed assets.'
  }
};

// Zakat al-Fitr rates by country (per person in local currency)
export const zakatFitrRates = {
  IN: { rate: 150, currency: 'INR', unit: 'per person' },
  AE: { rate: 25, currency: 'AED', unit: 'per person' },
  SA: { rate: 25, currency: 'SAR', unit: 'per person' },
  PK: { rate: 500, currency: 'PKR', unit: 'per person' },
  GB: { rate: 7, currency: 'GBP', unit: 'per person' },
  US: { rate: 12, currency: 'USD', unit: 'per person' },
  MY: { rate: 14, currency: 'MYR', unit: 'per person' },
  FR: { rate: 8, currency: 'EUR', unit: 'per person' },
  ID: { rate: 45000, currency: 'IDR', unit: 'per person' },
  BD: { rate: 120, currency: 'BDT', unit: 'per person' },
  TR: { rate: 150, currency: 'TRY', unit: 'per person' }
};

// Country-specific tax relief information
export const countryTaxInfo = {
  IN: {
    name: 'India',
    authority: 'Income Tax Department',
    relief: 'Zakat donations to eligible trusts qualify under Section 80G',
    maxRelief: '50% deduction on donated amount',
    notes: 'Donation must be to a registered charity/trust under 80G'
  },
  AE: {
    name: 'UAE',
    authority: 'Zakat Fund (Abu Dhabi/Dubai)',
    relief: 'No income tax; voluntary Zakat through official Zakat Fund',
    maxRelief: 'N/A (no income tax)',
    notes: 'Official Zakat Fund distributes to eligible categories'
  },
  SA: {
    name: 'Saudi Arabia',
    authority: 'ZATCA (Zakat, Tax and Customs Authority)',
    relief: 'Zakat is mandatory and collected by ZATCA from businesses',
    maxRelief: 'Full deduction from taxable base',
    notes: 'Businesses must file annual Zakat returns'
  },
  MY: {
    name: 'Malaysia',
    authority: 'LHDN (Inland Revenue Board)',
    relief: 'Zakat payments are tax deductible against tax payable',
    maxRelief: '100% of tax payable',
    notes: 'Zakat must be paid through official state Zakat authorities'
  },
  ID: {
    name: 'Indonesia',
    authority: 'BAZNAS',
    relief: 'Zakat is deductible from gross income',
    maxRelief: '2.5% of net income',
    notes: 'Must be paid through registered Amil Zakat institutions'
  },
  PK: {
    name: 'Pakistan',
    authority: 'FBR (Federal Board of Revenue)',
    relief: 'Zakat paid is allowed as deduction',
    maxRelief: 'Actual amount paid',
    notes: 'Zakat deducted at source on savings accounts'
  },
  GB: {
    name: 'United Kingdom',
    authority: 'HMRC',
    relief: 'Zakat to registered charities qualifies for Gift Aid',
    maxRelief: '25% extra from HMRC',
    notes: 'Charity must be UK registered for Gift Aid'
  },
  US: {
    name: 'United States',
    authority: 'IRS',
    relief: 'Zakat to 501(c)(3) organizations is tax deductible',
    maxRelief: 'Up to 60% of AGI for cash donations',
    notes: 'Must be paid to IRS-recognized charitable organization'
  },
  FR: {
    name: 'France',
    authority: 'DGFiP',
    relief: 'Donations to recognized associations qualify for tax reduction',
    maxRelief: '66% of donation, up to 20% of taxable income',
    notes: 'Association must be recognized as serving public interest'
  },
  ID: {
    name: 'Indonesia',
    authority: 'BAZNAS (Badan Amil Zakat Nasional)',
    relief: 'Zakat is deductible from gross income under Law No. 23/2011',
    maxRelief: '2.5% of net income',
    notes: 'Must be paid through registered Amil Zakat institutions (LAZ/BAZ)'
  },
  BD: {
    name: 'Bangladesh',
    authority: 'NBR (National Board of Revenue)',
    relief: 'Zakat donations are eligible for tax rebate',
    maxRelief: '15% tax rebate on actual investment/donation',
    notes: 'Donations must be to approved charitable institutions'
  },
  TR: {
    name: 'Turkey',
    authority: 'Diyanet (Presidency of Religious Affairs)',
    relief: 'Zakat paid to recognized foundations is tax-deductible',
    maxRelief: '5% of taxable income for individual donors',
    notes: 'Diyanet publishes official Fitr and Zakat rates annually'
  },

  // ---------- 29 newly-added countries (Feb 2026) ----------
  // Sources cite each jurisdiction\u2019s primary Zakat-collecting body, its
  // statutory status (mandatory state collection vs. voluntary mosque-led
  // distribution), and the relevant income-tax / charitable-donation
  // interaction. Verified against each country\u2019s most-recent finance act
  // / Awqaf bulletin as of Feb 2026.

  // ARABIC-SCRIPT NORTH AFRICA + MIDDLE EAST
  EG: { name: 'Egypt',
        authority: 'Dar al-Ift\u0101\u02bf al-Mi\u1e63riyyah + Bayt al-Zak\u0101t wa al-\u1e62adaq\u0101t (Nasser Bank)',
        relief: 'Donations to registered charities deductible up to 10 % of net taxable income under Law 91/2005 art. 23',
        maxRelief: '10 % of net taxable income',
        notes: 'Zakat is voluntary at the federal level. Bayt al-Zak\u0101t (operated through Nasser Social Bank) is the largest official distributor; mosque committees act locally.' },
  MA: { name: 'Morocco',
        authority: 'Minist\u00e8re des Habous et des Affaires Islamiques',
        relief: 'Donations to associations recognised as \u201cd\u2019utilit\u00e9 publique\u201d deductible under CGI art. 10',
        maxRelief: 'Actual amount paid, capped per CGI',
        notes: 'No state collection of Zakat. Habous Ministry oversees mosque-level collection; Conseil Sup\u00e9rieur des Ou\u00e9lmas issues annual fatwa references on Nisab.' },
  DZ: { name: 'Algeria',
        authority: 'Fonds de la Zakat (Caisse Nationale de la Zakat) — Minist\u00e8re des Affaires Religieuses',
        relief: 'Donations to the Fonds and recognised assoc deductible from IRG taxable base',
        maxRelief: '10 % of taxable income',
        notes: 'Voluntary; donors receive an official receipt from the Caisse for tax purposes. Fund distributes to the 8 Quranic categories.' },
  TN: { name: 'Tunisia',
        authority: 'Minist\u00e8re des Affaires Religieuses',
        relief: 'Charitable donations to recognised associations deductible under CIR art. 39',
        maxRelief: '2 % of net taxable income',
        notes: 'No state Zakat collection. Donations channelled through mosque committees and Croissant-Rouge Tunisien.' },
  LY: { name: 'Libya',
        authority: 'Diwan al-Zak\u0101t (since Law 89/1971) under the Awqaf Authority',
        relief: 'No formal tax relief — Zakat sits parallel to income tax',
        maxRelief: 'N/A',
        notes: 'Semi-formal state fund; collection has been fragmented post-2011 but Diwan al-Zak\u0101t still issues annual Nisab schedules.' },
  SD: { name: 'Sudan',
        authority: 'Diwan al-Zak\u0101t (Zakat Chamber) — statutory authority',
        relief: 'Zakat is a STATUTORY tax under the Zakat Act 2001; satisfies the Islamic obligation in lieu of the income tax base',
        maxRelief: '100 % credit against the Islamic-tax base',
        notes: 'One of the few countries with mandatory state-collected Zakat. Annual returns required.' },
  IQ: { name: 'Iraq',
        authority: 'Sunni and Shia Endowment Offices (Diwan al-Waqf al-Sunn\u012b / al-J\u0101\u02bffar\u012b)',
        relief: 'Donations to officially-recognised charities deductible under Income Tax Law 113/1982',
        maxRelief: '10 % of declared taxable income',
        notes: 'Voluntary; sect-based collection through each Diwan with separate fatwa councils.' },
  JO: { name: 'Jordan',
        authority: 'General Zakat Fund (\u015cand\u016bq al-Zak\u0101t al-\u02bf\u0100m, est. 1944) under the Ministry of Awqaf',
        relief: 'Donations to the General Zakat Fund deductible under Income Tax Law 34/2014 art. 12',
        maxRelief: '25 % of taxable income (individuals) / 10 % (corporates)',
        notes: 'Voluntary; the Fund publishes annual Nisab. Awqaf Ministry coordinates 14 governorate-level branches.' },
  LB: { name: 'Lebanon',
        authority: 'Dar al-Fatwa (Sunni) and Higher Ja\u02bffari Council (Shia)',
        relief: 'Donations to recognised public-interest associations deductible up to 4 % of declared income',
        maxRelief: '4 % of taxable income',
        notes: 'No state Zakat; sect-based collection through mosques and tariqas.' },
  SY: { name: 'Syria',
        authority: 'Ministry of Awqaf — Bayt al-Zak\u0101t (since 1962)',
        relief: 'Donations to the Bayt al-Zak\u0101t deductible up to 10 % of declared income (Law 24/2003)',
        maxRelief: '10 % of declared income',
        notes: 'Voluntary; operates through provincial mosque committees.' },
  PS: { name: 'Palestine',
        authority: 'Zakat Committees (Lij\u0101n al-Zak\u0101t) under the Ministry of Awqaf',
        relief: 'Donations to registered zakat committees deductible under PA Income Tax Law 8/2011',
        maxRelief: '20 % of declared income',
        notes: 'Voluntary; West Bank and Gaza operate parallel committees coordinated by the central Awqaf Ministry.' },
  YE: { name: 'Yemen',
        authority: 'Hay\u02bcat al-Zak\u0101t (statutory authority under Law 2/1999)',
        relief: 'Statutory Zakat sits alongside (and partially substitutes for) income tax',
        maxRelief: '100 % credit against the Islamic-tax base',
        notes: 'Mandatory by law (under Houthi-administered north; Bayt al-Zak\u0101t in Aden for the south). Yemen is one of the very few countries with statutory state-collected Zakat.' },
  KW: { name: 'Kuwait',
        authority: 'Bayt al-Zak\u0101t (House of Zakat, est. 1982) under the Ministry of Awqaf',
        relief: 'Voluntary contributions to Bayt al-Zak\u0101t and recognised charities are not income-deductible (no personal income tax)',
        maxRelief: 'N/A (no personal income tax)',
        notes: 'Highly-organised voluntary fund; corporates may deduct Zakat from the Zakat tax (1 % of profits per Law 46/2006) — the 1 % corporate Zakat is mandatory.' },
  QA: { name: 'Qatar',
        authority: 'Zakat Fund (\u015cand\u016bq al-Zak\u0101t) under the Ministry of Awqaf',
        relief: 'No personal income tax — Zakat is voluntary and not deducted from any tax base',
        maxRelief: 'N/A (no personal income tax)',
        notes: 'Well-organised voluntary fund; runs an online portal and annual Ramadan campaign. Distribution prioritises orphans and needy families.' },
  BH: { name: 'Bahrain',
        authority: 'Bahrain Zakat Fund under the Sunni Awqaf Directorate (and separate Ja\u02bffari Awqaf for Shia)',
        relief: 'No personal income tax — Zakat sits independently',
        maxRelief: 'N/A (no personal income tax)',
        notes: 'Sect-separated funds. Voluntary collection with strong banking-sector partnerships.' },
  OM: { name: 'Oman',
        authority: 'Bayt al-Zak\u0101t under the Ministry of Awqaf and Religious Affairs',
        relief: 'No personal income tax; corporate Zakat may interact with the 15 % corporate tax',
        maxRelief: 'N/A (no personal income tax)',
        notes: 'Smaller voluntary fund; Ibadi-aligned default fatwa references on collection priorities.' },
  MR: { name: 'Mauritania',
        authority: 'Caisse de la Zakat (Mauritanian Awqaf)',
        relief: 'Donations to recognised assoc deductible under CGI',
        maxRelief: 'Actual amount, capped per CGI',
        notes: 'Voluntary; Maliki-aligned distribution through local mahadras and zaouias.' },

  // FRANCOPHONE SUB-SAHARAN AFRICA — mostly mosque/association coordinated
  SN: { name: 'Senegal',
        authority: 'F\u00e9d\u00e9ration des Associations Islamiques + Tariqas (Tijaniyya, Mouridiyya)',
        relief: 'Donations to associations d\u2019utilit\u00e9 publique deductible under CGI art. 167',
        maxRelief: '0.50 % of turnover (companies) / 5 % of taxable income (individuals)',
        notes: 'Voluntary; significant Zakat flows through Sufi tariqa networks (Touba for Mouridiyya, Tivaouane for Tijaniyya).' },
  ML: { name: 'Mali',
        authority: 'Haut Conseil Islamique du Mali (HCI)',
        relief: 'Donations to officially recognised assoc deductible up to 5 % of income',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; HCI coordinates mosque-level collection.' },
  NE: { name: 'Niger',
        authority: 'Association Islamique du Niger + mosque committees',
        relief: 'Donations to public-utility assoc deductible under CGI',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; Maliki dominant.' },
  BF: { name: 'Burkina Faso',
        authority: 'F\u00e9d\u00e9ration des Associations Islamiques du Burkina (FAIB)',
        relief: 'Donations to associations reconnues deductible',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; mosque-coordinated.' },
  CI: { name: "C\u00f4te d'Ivoire",
        authority: 'COSIM (Conseil Sup\u00e9rieur des Imams) + CODIS (Conseil des Imams Sunnites)',
        relief: 'Donations to public-utility assoc deductible under CGI',
        maxRelief: '3 \u2030 (per mille) of turnover',
        notes: 'Voluntary; COSIM publishes annual Nisab references.' },
  TD: { name: 'Chad',
        authority: 'Conseil Sup\u00e9rieur des Affaires Islamiques (CSAI)',
        relief: 'Donations to public-utility assoc deductible',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; CSAI coordinates collection.' },
  CM: { name: 'Cameroon',
        authority: 'Association Culturelle Islamique du Cameroun (ACIC)',
        relief: 'Donations to associations reconnues d\u2019utilit\u00e9 publique deductible',
        maxRelief: '0.50 % of turnover (companies) / 5 % of income (individuals)',
        notes: 'Voluntary; concentrated in the predominantly-Muslim north (Adamaoua, Nord, Extr\u00eame-Nord).' },
  GN: { name: 'Guinea',
        authority: 'Secr\u00e9tariat G\u00e9n\u00e9ral aux Affaires Religieuses',
        relief: 'Donations to assoc reconnues deductible under CGI',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; Maliki dominant.' },
  BJ: { name: 'Benin',
        authority: 'Union Islamique du B\u00e9nin (UIB)',
        relief: 'Donations to associations reconnues d\u2019utilit\u00e9 publique deductible',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; coordinated mostly in the northern districts of Borgou and Alibori.' },

  // MIXED ARABIC/FRENCH (Comoros, Djibouti)
  KM: { name: 'Comoros',
        authority: 'Mufti of the Comoros + Ministry of Justice and Islamic Affairs',
        relief: 'Donations to assoc reconnues deductible under CGI',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; Shafi\u02bfi-aligned distribution priorities.' },
  DJ: { name: 'Djibouti',
        authority: 'Minist\u00e8re des Affaires Musulmanes, de la Culture et des Biens Waqfs',
        relief: 'Donations to assoc reconnues deductible under CGI',
        maxRelief: '5 % of taxable income',
        notes: 'Voluntary; Shafi\u02bfi-aligned, coordinated through the Awqaf Ministry.' },

  // NORTH AMERICA / EUROPE (NEW)
  CA: { name: 'Canada',
        authority: 'Canada Revenue Agency (CRA)',
        relief: 'Donations to CRA-registered charities qualify for both federal and provincial non-refundable tax credits',
        maxRelief: '\u224829 % federal credit + 11\u201324 % provincial (varies by province) on the donated amount',
        notes: 'Charity must be a CRA-registered Qualified Donee; first-time donors get an extra 25 % super-credit on the first $1,000.' },
  BE: { name: 'Belgium',
        authority: 'SPF Finances',
        relief: 'Donations to recognised associations qualify for a 45 % income-tax reduction (Code des imp\u00f4ts art. 145/33)',
        maxRelief: '45 % of donated amount, capped at 10 % of net income or \u20ac397,850',
        notes: 'Minimum donation \u20ac40/year; association must be on the official list maintained by SPF Finances.' },

  // ---------- Feb 2026: Nigeria · Iran · Afghanistan · Brunei ----------
  NG: { name: 'Nigeria',
        authority: 'Nigerian Supreme Council for Islamic Affairs (NSCIA) · State Zakat & \u1e62adaqah Boards (Sokoto, Kano, Zamfara, Bauchi, Borno, Jigawa, Kaduna)',
        relief: 'Donations to charities registered under the Companies Allied Matters Act / CITA s. 25 are deductible from taxable profit; individual donors get a tax credit under PITA Sched. 6',
        maxRelief: '10 % of taxable profit (corporate) / actual donation amount (individual)',
        notes: 'Voluntary nationally. The 12 northern Sharia-implementing states each run a State Zakat & \u1e62adaqah Board — Kano and Zamfara collect tens of billions of Naira annually. NSCIA publishes the national Nisab in NGN every Ramadan.' },

  IR: { name: 'Iran',
        authority: 'Imam Khomeini Relief Foundation (Komite Emdad) · Marja\u2018 offices (Sistani, Khamenei) · Local Hay\u2019at',
        relief: 'No formal income-tax deduction for Zakat at the federal level. Khums (the 20 % Shi\u2018a obligation) is voluntary and paid directly to a Marja\u2018 office.',
        maxRelief: 'N/A (no formal tax deduction)',
        notes: 'Ja\u02bfari fiqh distinguishes Zakat al-Fitr (mandatory on staple food per head) and Zakat on the 9 classical items (gold, silver, wheat, barley, dates, raisins, camels, cattle, sheep). Modern cash wealth is typically routed through Khums (20 %) rather than Zakat (2.5 %). Verify the calculation with your Marja\u2018 of taqlid.' },

  AF: { name: 'Afghanistan',
        authority: 'Ministry of Hajj & Religious Affairs · Local mosque \u02bfulama councils',
        relief: 'No formal income-tax deduction; voluntary individual giving distributed through mosques.',
        maxRelief: 'N/A (no formal tax framework currently in force)',
        notes: 'Zakat collection has historically been informal and mosque-led. The Ministry of Hajj publishes annual Nisab guidance in AFN. Significant remittance inflows from the diaspora are typically routed via the Da Afghanistan Bank or hawala networks.' },

  BN: { name: 'Brunei',
        authority: 'Majlis Ugama Islam Brunei (MUIB) — Bahagian Zakat',
        relief: 'No income tax in Brunei. Zakat al-mal is mandatory for adult Muslim citizens and channelled via MUIB.',
        maxRelief: 'N/A (no income tax)',
        notes: 'MUIB publishes the Nisab and per-head Zakat al-Fitr in BND each Ramadan. Eight Quranic categories of recipients (asnaf) are administered through MUIB welfare programmes. Sultan Hassanal Bolkiah personally launches the annual collection drive.' },

  // ---------- Feb 2026: Kenya · Tanzania · Uganda · Ethiopia · Somalia · Sri Lanka · Philippines · China ----------
  KE: { name: 'Kenya',
        authority: 'Supreme Council of Kenya Muslims (SUPKEM) · Kenya Muslim Charitable Society',
        relief: 'Donations to registered charities under the Income Tax Act s. 16(2)(p) are deductible against business income; individual donors get no automatic deduction.',
        maxRelief: 'Actual amount donated (corporate); none for individuals',
        notes: 'SUPKEM publishes annual Nisab guidance in KES. Coastal communities follow Shafi\u2018i; SUPKEM provides a national fatwa channel.' },

  TZ: { name: 'Tanzania',
        authority: 'BAKWATA (National Muslim Council of Tanzania) · Office of the Mufti of Zanzibar',
        relief: 'Donations to entities registered under the Income Tax Act 2004 s. 12 are deductible against taxable income for corporates; individual donors receive no deduction.',
        maxRelief: '2% of taxable income (corporate)',
        notes: 'BAKWATA (mainland) and the Mufti\'s Office (Zanzibar) publish parallel annual Nisab in TZS each Ramadan.' },

  UG: { name: 'Uganda',
        authority: 'Uganda Muslim Supreme Council (UMSC) — Department of Zakat',
        relief: 'Donations to charitable organisations registered under the Income Tax Act Cap. 340 s. 34 are deductible against business income for corporates.',
        maxRelief: '5% of taxable income (corporate)',
        notes: 'UMSC under the Mufti of Uganda publishes annual Nisab in UGX. Strong tradition of mosque-led collection during Ramadan, including the daily Iftar fund.' },

  ET: { name: 'Ethiopia',
        authority: 'Ethiopian Islamic Affairs Supreme Council (EIASC / Mejlis) · Federal Sharia Court',
        relief: 'No formal Zakat tax-deduction at the federal level. Mejlis collection runs as a parallel voluntary system through regional councils.',
        maxRelief: 'N/A',
        notes: 'Mejlis publishes annual Nisab in ETB. Regional councils (Addis Ababa, Harari, Afar, Somali) administer collection. Distribution prioritises orphans, displaced persons and madrasa students.' },

  SO: { name: 'Somalia',
        authority: 'Ministry of Religious Affairs (MoRA) · Local mosque \u02bfulama councils',
        relief: 'No formal income tax framework currently active. Voluntary collection through mosques and local charities (Zamzam Foundation, Hidaya Foundation).',
        maxRelief: 'N/A',
        notes: 'Significant remittance-driven collection (~$2B/year diaspora inflows) is informally tied to Zakat. Hormuud-Pay and Dahabshiil offer in-app Zakat-routing.' },

  LK: { name: 'Sri Lanka',
        authority: 'All Ceylon Jamiyyathul Ulama (ACJU) — Bayt al-Mal · Department of Muslim Religious & Cultural Affairs',
        relief: 'Donations to charities approved under the Inland Revenue Act s. 34(b) are deductible from assessable income.',
        maxRelief: 'One-third of assessable income or LKR 75,000, whichever is less',
        notes: 'ACJU publishes annual Nisab in LKR. Eastern Province communities (Kalmunai, Akkaraipattu, Ampara) have the highest per-capita Zakat collection density nationally.' },

  PH: { name: 'Philippines',
        authority: 'National Commission on Muslim Filipinos (NCMF) · Bangsamoro Darul-Ifta',
        relief: 'Donations to NCMF-accredited Muslim charities qualify for a deduction under NIRC s. 34(H) — full deduction for corporate donors.',
        maxRelief: '10% of taxable income for individuals; full amount for corporate donors to accredited NGOs',
        notes: 'NCMF publishes annual Nisab in PHP each Ramadan. BARMM Bayt al-Mal manages institutional collection across Bangsamoro Region.' },

  CN: { name: 'China',
        authority: 'China Islamic Association (\u4e2d\u56fd\u4f0a\u65af\u5170\u6559\u534f\u4f1a) · Provincial Imams\u2019 Councils',
        relief: 'No formal Zakat tax-deduction. Charitable donations to state-approved organisations under the Charity Law 2016 qualify for partial deduction (up to 30% of annual income).',
        maxRelief: '30% of annual income (general charity, not Zakat-specific)',
        notes: 'Local imams administer Zakat collection at the mosque level. State oversight is heavy; consult provincial-level Imams\u2019 Council for current Nisab and channels.' },

  // ---------- Feb 2026 — Round 4: 22 new countries ----------
  ZA: { name: 'South Africa',
        authority: 'United Ulama Council of SA (UUCSA) · Muslim Judicial Council · SANZAF (South African National Zakah Fund)',
        relief: 'Donations to s.18A-approved PBOs deductible against taxable income.',
        maxRelief: '10% of taxable income',
        notes: 'SANZAF is the largest national Zakat distributor; UUCSA publishes annual Nisab in ZAR each Ramadan.' },

  TH: { name: 'Thailand',
        authority: 'Sheikhul Islam Office · Central Islamic Council of Thailand',
        relief: 'Donations to RD-approved charities under Revenue Code s. 47(7) deductible.',
        maxRelief: '10% of net income (individual)',
        notes: 'Provincial Islamic Council Zakat collection concentrated in the 4 southern provinces.' },

  MM: { name: 'Myanmar',
        authority: 'Islamic Religious Affairs Council of Myanmar · Local mosque-based collection',
        relief: 'No formal deduction system currently functional.',
        maxRelief: 'N/A',
        notes: 'Mosque-led collection. Rohingya-related disruptions affect documentation. Diaspora remittance flows substantial.' },

  MV: { name: 'Maldives',
        authority: 'Ministry of Islamic Affairs — Bait al-Mal',
        relief: 'No income tax in the Maldives for individuals. Zakat al-mal mandatory for qualifying adults via Bait al-Mal.',
        maxRelief: 'N/A',
        notes: 'Ministry of Islamic Affairs publishes annual Nisab in MVR. Distribution exclusively through the official Bait al-Mal.' },

  DE: { name: 'Germany',
        authority: 'Zentralrat der Muslime in Deutschland (ZMD) · Islamische Zeitung\u2019s Zakat fund · Islamic Relief Germany',
        relief: 'Donations to gemeinnützig-recognised charities deductible under EStG § 10b.',
        maxRelief: '20% of total income',
        notes: 'No state-collected Zakat. Multiple Verein-based collection channels operate; verify gemeinnützig certificate before donating.' },

  IT: { name: 'Italy',
        authority: 'Unione delle Comunità Islamiche d\u2019Italia (UCOII) · Centro Islamico Culturale d\u2019Italia',
        relief: 'Donations to ONLUS / ETS organisations deductible at 30% (capped €30K) or as a 19% tax credit.',
        maxRelief: '€30,000 / year (deduction track)',
        notes: 'UCOII coordinates national Zakat distribution drives; Centro Islamico Culturale Roma is the largest single channel.' },

  ES: { name: 'Spain',
        authority: 'Unión de Comunidades Islámicas de España (UCIDE) · Comisión Islámica de España · Islamic Relief Spain',
        relief: 'Donations to entities under Ley 49/2002 deductible at 80% of first €250 + 40% of remainder.',
        maxRelief: '10% of taxable base',
        notes: 'Generous incentive structure means Zakat-routed donations effectively recover ~40% of the donation as a credit.' },

  NL: { name: 'Netherlands',
        authority: 'Contactorgaan Moslims en Overheid (CMO) · Islamic Relief Netherlands',
        relief: 'Donations to ANBI-recognised charities deductible under Wet IB s. 6.32.',
        maxRelief: '10% of threshold income',
        notes: 'Many Dutch masjids hold ANBI status — verify before donating to qualify for the deduction.' },

  CH: { name: 'Switzerland',
        authority: 'Fédération d\u2019organisations islamiques de Suisse (FOIS) · VIOZ',
        relief: 'Donations to charities recognised as "tax-exempt for public service" deductible federally + cantonally.',
        maxRelief: '20% of federal taxable income (cantonal varies)',
        notes: 'Cantonal differences are significant — Zurich, Geneva, Bern have the largest Islamic charities.' },

  AT: { name: 'Austria',
        authority: 'Islamische Glaubensgemeinschaft in Österreich (IGGÖ)',
        relief: 'Donations to "begünstigte Einrichtungen" deductible under EStG § 4a.',
        maxRelief: '10% of total income',
        notes: 'IGGÖ is constitutionally recognised — issues annual Nisab in EUR; many member-Vereine qualify as deductible recipients.' },

  SE: { name: 'Sweden',
        authority: 'Islamiska Förbundet i Sverige (IFiS) · Sveriges Imamråd · Islamic Relief Sweden',
        relief: 'No general donation deduction since 2016 abolition. Limited gåvoavdrag exists for specific approved orgs (max SEK 6,000/year).',
        maxRelief: 'SEK 6,000 / year (where applicable)',
        notes: 'IRS-equivalent (Skatteverket) charitable deduction is very limited — most Swedish Muslims pay Zakat without seeking deduction.' },

  DK: { name: 'Denmark',
        authority: 'Dansk Islamisk Råd · Muslimernes Fællesråd',
        relief: 'Donations to approved charities deductible under LL § 8A (max DKK 18,300/year/individual).',
        maxRelief: 'DKK 18,300 / year (2026)',
        notes: 'Verify the receiving charity holds § 8A approval each year — list maintained by Skattestyrelsen.' },

  NO: { name: 'Norway',
        authority: 'Islamic Council Norway (Islamsk Råd Norge)',
        relief: 'Donations to approved religious / charitable organisations deductible up to NOK 25,000 (2026).',
        maxRelief: 'NOK 25,000 / year',
        notes: 'Islamic Council Norway publishes annual Nisab in NOK. Several mosques hold approved-recipient status.' },

  AL: { name: 'Albania',
        authority: 'Komuniteti Mysliman i Shqipërisë (KMSH)',
        relief: 'Limited charity deduction under Law 8438. KMSH operates the national Bait al-Mal.',
        maxRelief: 'Discretionary; typically up to 4% of income',
        notes: 'KMSH publishes annual Nisab in ALL and EUR for the diaspora.' },

  BA: { name: 'Bosnia & Herzegovina',
        authority: 'Islamska Zajednica u BiH — Bejtu-l-Mal',
        relief: 'Recognised religious community status allows Zekat to flow through Bejtu-l-Mal with full transparency.',
        maxRelief: 'Discretionary deduction under Federation + RS tax laws',
        notes: 'BIH operates the largest Bait al-Mal in the Balkans. Annual Nisab in BAM published by the Rijaset.' },

  XK: { name: 'Kosovo',
        authority: 'Bashkësia Islame e Kosovës (BIK)',
        relief: 'Donations to BIK-approved charities partially deductible under Law No. 03/L-162.',
        maxRelief: 'Up to 5% of taxable income',
        notes: 'BIK publishes annual Nisab in EUR.' },

  MK: { name: 'North Macedonia',
        authority: 'Islamic Religious Community of North Macedonia',
        relief: 'Charitable donations deductible up to 5% of taxable income.',
        maxRelief: '5% of taxable income',
        notes: 'Annual Nisab published in MKD.' },

  ME: { name: 'Montenegro',
        authority: 'Islamska Zajednica u Crnoj Gori',
        relief: 'Limited charitable deduction; verify with the Tax Authority each year.',
        maxRelief: 'Discretionary',
        notes: 'Smaller Muslim community than other Balkan states; central Bait al-Mal in Podgorica.' },

  RS: { name: 'Serbia',
        authority: 'Islamic Community of Serbia (Islamska Zajednica Srbije)',
        relief: 'Donations to recognised religious communities deductible up to 5% of taxable income.',
        maxRelief: '5% of taxable income',
        notes: 'Strongest collection presence in the Sandžak region (Novi Pazar).' },

  HR: { name: 'Croatia',
        authority: 'Islamska Zajednica u Hrvatskoj',
        relief: 'Donations to recognised charities deductible up to 2% of prior-year income.',
        maxRelief: '2% of prior-year income',
        notes: 'EU member state — donor-funded distribution through the Zagreb Islamic Centre.' },

  SI: { name: 'Slovenia',
        authority: 'Islamska skupnost v RS',
        relief: 'Donations deductible up to 0.3% of taxable income.',
        maxRelief: '0.3% of taxable income',
        notes: 'Ljubljana Islamic Cultural Centre is the main collection hub.' },

  BG: { name: 'Bulgaria',
        authority: 'Главно мюфтийство (Chief Mufti\'s Office of Bulgaria)',
        relief: 'Donations to recognised religious communities deductible up to 10% of taxable income.',
        maxRelief: '10% of taxable income',
        notes: 'Chief Mufti\'s Office publishes annual Nisab in BGN. Strong collection presence in the Kardzhali + Razgrad regions.' },
};

// Scholarly rulings comparison
export const scholarlyComparison = {
  nisab: {
    hanafi: { standard: 'silver', grams: 612.36, note: 'Uses silver nisab to benefit more poor' },
    maliki: { standard: 'gold', grams: 85, note: 'Gold nisab is preferred' },
    shafii: { standard: 'gold', grams: 85, note: 'Gold nisab for monetary wealth' },
    hanbali: { standard: 'gold', grams: 85, note: 'Gold standard for currency' },
    aaoifi: { standard: 'both', note: 'Recommends whichever is lower to benefit poor' }
  },
  jewellery: {
    hanafi: 'Zakat due on all gold/silver jewellery if reaches nisab',
    maliki: 'Jewellery worn regularly is exempt',
    shafii: 'Permissible jewellery for women is exempt',
    hanbali: 'Similar to Shafi\'i - regular wear exempt',
    aaoifi: 'Follow local scholarly opinion'
  },
  stockValuation: {
    hanafi: 'Value at cost price',
    maliki: 'Value at market (selling) price',
    shafii: 'Market value preferred',
    hanbali: 'Market value',
    aaoifi: 'Current market value'
  }
};
